import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  DisplayText,
  HStack,
  Modal,
  QRCodeScanner,
  theme,
} from '@sweetgreen/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import { ScanToPayCustomerPass } from './ScanToPay.customerPass';
import { useScanToPayNavigation } from './ScanToPay.navigation';

export const UsageHint = () => {
  const { t } = useLocalizationContext();

  return (
    <View style={styles.padding}>
      <DisplayText size={4}>
        {t('account.scan-to-pay.header.title')}
      </DisplayText>
      <BodyText size={4}>{t('account.scan-to-pay.header.subtitle')}</BodyText>
    </View>
  );
};

export const Footer = () => {
  const { t } = useLocalizationContext();
  const { goBack } = useScanToPayNavigation();
  const showCustomerPass = useIsAppleWalletEnabled();

  return (
    <Modal.Footer>
      <HStack
        shouldFillAvailableSpace={false}
        itemsPerRow={showCustomerPass ? 2 : 1}
        style={styles.footer}
      >
        <Button
          width="100%"
          size="large"
          accessibilityRole="button"
          accessibilityLabel={t('general.close')}
          accessibilityHint={t('home.navigation')}
          onPress={goBack}
          palette="secondary"
        >
          {t('general.close')}
        </Button>
        {showCustomerPass ? <ScanToPayCustomerPass /> : null}
      </HStack>
    </Modal.Footer>
  );
};

export const ScannerComponent = ({
  onPromoCodeScanned,
  handleHideScanner,
}: {
  readonly onPromoCodeScanned: (code: string) => void;
  readonly handleHideScanner: () => void;
}) => {
  const { t } = useLocalizationContext();
  const [requestPermissionResponse, onRequestPermissionResponse] =
    React.useState(false);

  return (
    <View testID="scan-to-pay.gift-code.scanner" style={styles.scanner}>
      <QRCodeScanner
        onRequestPermissionResponse={onRequestPermissionResponse}
        onScanSuccess={onPromoCodeScanned}
      />
      <View style={styles.scannerMessageContainer}>
        <DisplayText style={styles.text} size={4}>
          {t(
            requestPermissionResponse
              ? 'account.scan-to-pay.scan-gift-code-message'
              : 'account.scan-to-pay.scan-gift-code-camera-permission-message',
          )}
        </DisplayText>
        <Button size="large" onPress={handleHideScanner}>
          {t('general.cancel')}
        </Button>
      </View>
    </View>
  );
};

// Feature flag + platform check for apple wallet integration.
const useIsAppleWalletEnabled = () => {
  const isFeatureFlagOn = useFeatureFlag('permanent-apple-wallet-enabled');

  return Platform.OS === 'ios' && isFeatureFlagOn;
};

const styles = StyleSheet.create({
  padding: {
    paddingVertical: theme.spacing['4'],
    paddingHorizontal: theme.spacing['2'],
  },
  footer: {
    alignItems: 'flex-end',
  },
  scanner: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: theme.colors.BLACK,
  },
  scannerMessageContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: theme.spacing['4'],
    justifyContent: 'space-evenly',
    backgroundColor: theme.colors.OATMEAL,
    borderTopLeftRadius: theme.radius.medium,
    borderTopRightRadius: theme.radius.medium,
    paddingHorizontal: theme.spacing['4'],
  },
  text: {
    paddingBottom: theme.spacing['4'],
  },
});
